/**
 * What the entry screen may say about the microphone before anybody presses
 * anything.
 *
 * The room does not capture on the way in — `useRoom` opens the device after
 * the room connects — so a refused microphone has never actually kept anybody
 * out. What it does is keep quiet about it until the room can say what failed.
 *
 * Deliberately conservative. It reports a refusal only where the browser states
 * one, because the alternative is asking for the device to find out, and a
 * permission prompt nobody asked for is worse than a late one. Firefox and
 * Safari have not always answered for `microphone` at all, and an unanswered
 * query is not a refusal.
 */

/** What the screen knows about the microphone, before the room is entered. */
export type MicrophoneAccess = "granted" | "refused" | "unknown";

/**
 * @param state what the Permissions API said, or `undefined` where it did not
 *   answer — an unsupported name, an older browser, a query that threw
 */
export function microphoneAccessFrom(
  state: PermissionState | undefined,
): MicrophoneAccess {
  if (state === "denied") return "refused";
  if (state === "granted") return "granted";
  // `prompt` is not a refusal: nobody has been asked yet, and the honest thing
  // is to let them press the button that asks.
  return "unknown";
}

/**
 * Whether the way in should be offered as listening rather than as speaking.
 *
 * Only a stated refusal changes the offer. Everything else keeps the button the
 * visitor already knows, because a screen that hedges about the microphone on
 * every visit teaches people to ignore it.
 */
export function entersAsListener(access: MicrophoneAccess): boolean {
  return access === "refused";
}
